import React, { useState, useRef } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Grid,
  TextField,
  CircularProgress,
  Alert,
  Divider,
  Chip
} from '@mui/material';
import {
  Print as PrintIcon,
  GetApp as DownloadIcon,
  Search as SearchIcon,
  Assignment as ReportIcon,
  CheckCircle as AcceptedIcon,
  Cancel as RejectedIcon
} from '@mui/icons-material';
import { useQuery } from 'react-query';
import { useSnackbar } from 'notistack';
import { useAuth } from '../../contexts/AuthContext';
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';

const RawMaterialTestingReport = () => {
  const [searchParams, setSearchParams] = useState({
    srr_no: '',
    heat_no: '',
    tc_no: ''
  });
  
  const [reportData, setReportData] = useState(null);
  const reportRef = useRef(null);
  const { enqueueSnackbar } = useSnackbar();
  const { api } = useAuth();

  const { isLoading, refetch } = useQuery(
    ['rmTestingReport', searchParams],
    async () => {
      if (!searchParams.srr_no && !searchParams.heat_no && !searchParams.tc_no) {
        throw new Error('Please provide at least one search parameter');
      }
      
      const params = new URLSearchParams();
      Object.entries(searchParams).forEach(([key, value]) => {
        if (value) params.append(key, value);
      });
      
      const response = await api.get(`/reports/rm-testing?${params}`);
      setReportData(response.data);
      return response.data;
    },
    {
      enabled: false,
      onError: (error) => {
        enqueueSnackbar(`Error fetching report: ${error.message}`, { variant: 'error' });
      }
    }
  );

  const handleSearch = () => {
    refetch();
  };

  const handlePrint = () => {
    window.print();
  };

  const handleExportPDF = async () => {
    if (!reportRef.current) {
      enqueueSnackbar('No report to export', { variant: 'warning' });
      return;
    }

    try {
      enqueueSnackbar('Generating PDF...', { variant: 'info' });
      
      const canvas = await html2canvas(reportRef.current, {
        scale: 2,
        logging: false,
        useCORS: true,
        backgroundColor: '#ffffff'
      });
      
      const imgData = canvas.toDataURL('image/png');
      const pdf = new jsPDF({
        orientation: 'landscape',
        unit: 'mm',
        format: 'a4'
      });
      
      const imgWidth = 297; // A4 landscape width in mm
      const imgHeight = (canvas.height * imgWidth) / canvas.width;
      
      pdf.addImage(imgData, 'PNG', 0, 0, imgWidth, imgHeight);
      pdf.save(`RM_Testing_Report_${searchParams.srr_no || reportData?.srr_no || 'export'}.pdf`);
      
      enqueueSnackbar('PDF exported successfully', { variant: 'success' });
    } catch (error) {
      enqueueSnackbar(`PDF export failed: ${error.message}`, { variant: 'error' });
    }
  };

  // Mock data for demonstration (replace with actual API data)
  const mockReportData = reportData || {
    srr_no: '500001',
    srr_date: '2025-04-02',
    tc_no: '86125017F',
    heat_no: 'C84957',
    mill_name: 'TATA STEEL LONG PRODUCTS LIMITED',
    mill_code: 'TATA',
    steel_code: '40Cr4',
    specification: 'IS:40Cr4',
    section: '90 DIA',
    route: 'BF/EAF/LRF/VD/C',
    quantity_nos: 26,
    weight: 7.680,
    sample_nos: 0,
    marking: 'OK',
    heat_marking: 'C R S',
    mill_grain_size: 0.0,
    astm_grain_size: null,
    
    // Chemical composition
    chemical: {
      spec_min: {
        C: 0.35, Mn: 0.60, P: null, S: null, Si: null, Cr: 0.90, Ni: null, Mo: null, Al: null, V: null
      },
      spec_max: {
        C: 0.45, Mn: 0.90, P: 0.035, S: 0.035, Si: 0.350, Cr: 1.20, Ni: null, Mo: 0.040, Al: null, V: null
      },
      mill: {
        C: 0.39, Mn: 0.86, P: 0.012, S: 0.025, Si: 0.260, Cr: 1.13, Ni: null, Mo: 0.027, Al: null, V: null
      }
    },
    
    // Hardness test results
    hardness: {
      visual_inspection: 'OK',
      as_recd_micro: true,
      macro_test_result: 'MACRO TEST RESULT',
      samples: [
        { sample_no: 'Spec.Min', dimension: null, hardness: null, case: null, core: null, inclusions_A: 'T/H', inclusions_B: 'T/H', inclusions_C: 'T/H', inclusions_D: 'T/H' },
        { sample_no: 'Spec.Max', dimension: '+1%', hardness: null, case: null, core: '2.0/1.5', inclusions_A: '2.0/1.5', inclusions_B: '2.0/1.5', inclusions_C: '2.0/1.5', inclusions_D: '2.0/1.5' },
        { sample_no: 'MILL', dimension: null, hardness: null, case: null, core: '1.5/0.5', inclusions_A: '0.5/0.0', inclusions_B: '0.0/0.0', inclusions_C: '1.0/0.0', inclusions_D: null }
      ],
      core_hardness: [90.71, 90.76, 90.70, 90.74, 90.75]
    },
    
    // Mechanical properties
    mechanical: {
      tensile_strength: null,
      yield_strength: null,
      elongation: null,
      area_reduction: null
    },
    
    // Jominy hardenability
    jominy: {
      dist: [], 
      mill: [],
      kfl: []
    },
    
    // Quality observations
    quality: {
      surface_quality: {
        first_sample: 2,
        bars: null
      },
      pickling_grinding: {
        percent_100: true,
        rejected_bars: 'Nos'
      },
      reinspection_bars: null,
      step_down_test: null
    },
    
    // Remarks and status
    remarks: 'Repeat heat as per original Serial Control 2400256 & having status ACCEPTED',
    status: 'ACCEPTED',
    authorized_sign: null,
    date: '2025-04-02',
    qa_fr_26: true
  };

  const displayData = reportData || mockReportData;

  const renderChemicalComposition = () => (
    <TableContainer>
      <Table size="small" sx={{ border: '1px solid #000' }}>
        <TableHead>
          <TableRow>
            <TableCell sx={{ border: '1px solid #000', fontWeight: 'bold' }}>SAMPLE NO</TableCell>
            <TableCell sx={{ border: '1px solid #000', fontWeight: 'bold' }}>DIMENSION (mm)</TableCell>
            <TableCell sx={{ border: '1px solid #000', fontWeight: 'bold' }}>HARDNESS CASE/CORE</TableCell>
            <TableCell colSpan={4} align="center" sx={{ border: '1px solid #000', fontWeight: 'bold' }}>INCLUSIONS</TableCell>
            <TableCell sx={{ border: '1px solid #000', fontWeight: 'bold' }}>C%</TableCell>
            <TableCell sx={{ border: '1px solid #000', fontWeight: 'bold' }}>Mn%</TableCell>
            <TableCell sx={{ border: '1px solid #000', fontWeight: 'bold' }}>P%</TableCell>
            <TableCell sx={{ border: '1px solid #000', fontWeight: 'bold' }}>S%</TableCell>
            <TableCell sx={{ border: '1px solid #000', fontWeight: 'bold' }}>Si%</TableCell>
            <TableCell sx={{ border: '1px solid #000', fontWeight: 'bold' }}>Cr%</TableCell>
            <TableCell sx={{ border: '1px solid #000', fontWeight: 'bold' }}>Ni%</TableCell>
            <TableCell sx={{ border: '1px solid #000', fontWeight: 'bold' }}>Mo%</TableCell>
            <TableCell sx={{ border: '1px solid #000', fontWeight: 'bold' }}>Al%</TableCell>
            <TableCell sx={{ border: '1px solid #000', fontWeight: 'bold' }}>V%</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          <TableRow>
            <TableCell sx={{ border: '1px solid #000' }}>Spec.Min</TableCell>
            <TableCell sx={{ border: '1px solid #000' }}></TableCell>
            <TableCell sx={{ border: '1px solid #000' }}></TableCell>
            <TableCell sx={{ border: '1px solid #000' }}>A</TableCell>
            <TableCell sx={{ border: '1px solid #000' }}>B</TableCell>
            <TableCell sx={{ border: '1px solid #000' }}>C</TableCell>
            <TableCell sx={{ border: '1px solid #000' }}>D</TableCell>
            <TableCell sx={{ border: '1px solid #000' }}>{displayData.chemical.spec_min.C || '-'}</TableCell>
            <TableCell sx={{ border: '1px solid #000' }}>{displayData.chemical.spec_min.Mn || '-'}</TableCell>
            <TableCell sx={{ border: '1px solid #000' }}>{displayData.chemical.spec_min.P || '-'}</TableCell>
            <TableCell sx={{ border: '1px solid #000' }}>{displayData.chemical.spec_min.S || '-'}</TableCell>
            <TableCell sx={{ border: '1px solid #000' }}>{displayData.chemical.spec_min.Si || '-'}</TableCell>
            <TableCell sx={{ border: '1px solid #000' }}>{displayData.chemical.spec_min.Cr || '-'}</TableCell>
            <TableCell sx={{ border: '1px solid #000' }}>{displayData.chemical.spec_min.Ni || '-'}</TableCell>
            <TableCell sx={{ border: '1px solid #000' }}>{displayData.chemical.spec_min.Mo || '-'}</TableCell>
            <TableCell sx={{ border: '1px solid #000' }}>{displayData.chemical.spec_min.Al || '-'}</TableCell>
            <TableCell sx={{ border: '1px solid #000' }}>{displayData.chemical.spec_min.V || '-'}</TableCell>
          </TableRow>
          <TableRow>
            <TableCell sx={{ border: '1px solid #000' }}>Spec.Max</TableCell>
            <TableCell sx={{ border: '1px solid #000' }}>+1%</TableCell>
            <TableCell sx={{ border: '1px solid #000' }}></TableCell>
            <TableCell sx={{ border: '1px solid #000' }}>2.0/1.5</TableCell>
            <TableCell sx={{ border: '1px solid #000' }}>2.0/1.5</TableCell>
            <TableCell sx={{ border: '1px solid #000' }}>2.0/1.5</TableCell>
            <TableCell sx={{ border: '1px solid #000' }}>2.0/1.5</TableCell>
            <TableCell sx={{ border: '1px solid #000' }}>{displayData.chemical.spec_max.C || '-'}</TableCell>
            <TableCell sx={{ border: '1px solid #000' }}>{displayData.chemical.spec_max.Mn || '-'}</TableCell>
            <TableCell sx={{ border: '1px solid #000' }}>{displayData.chemical.spec_max.P || '-'}</TableCell>
            <TableCell sx={{ border: '1px solid #000' }}>{displayData.chemical.spec_max.S || '-'}</TableCell>
            <TableCell sx={{ border: '1px solid #000' }}>{displayData.chemical.spec_max.Si || '-'}</TableCell>
            <TableCell sx={{ border: '1px solid #000' }}>{displayData.chemical.spec_max.Cr || '-'}</TableCell>
            <TableCell sx={{ border: '1px solid #000' }}>{displayData.chemical.spec_max.Ni || '-'}</TableCell>
            <TableCell sx={{ border: '1px solid #000' }}>{displayData.chemical.spec_max.Mo || '-'}</TableCell>
            <TableCell sx={{ border: '1px solid #000' }}>{displayData.chemical.spec_max.Al || '-'}</TableCell>
            <TableCell sx={{ border: '1px solid #000' }}>{displayData.chemical.spec_max.V || '-'}</TableCell>
          </TableRow>
          <TableRow>
            <TableCell sx={{ border: '1px solid #000', bgcolor: '#f0f0f0' }}>MILL</TableCell>
            <TableCell sx={{ border: '1px solid #000', bgcolor: '#f0f0f0' }}></TableCell>
            <TableCell sx={{ border: '1px solid #000', bgcolor: '#f0f0f0' }}></TableCell>
            <TableCell sx={{ border: '1px solid #000', bgcolor: '#f0f0f0' }}>1.5/0.5</TableCell>
            <TableCell sx={{ border: '1px solid #000', bgcolor: '#f0f0f0' }}>0.5/0.0</TableCell>
            <TableCell sx={{ border: '1px solid #000', bgcolor: '#f0f0f0' }}>0.0/0.0</TableCell>
            <TableCell sx={{ border: '1px solid #000', bgcolor: '#f0f0f0' }}>1.0/0.0</TableCell>
            <TableCell sx={{ border: '1px solid #000', bgcolor: '#f0f0f0', fontWeight: 'bold' }}>{displayData.chemical.mill.C || '-'}</TableCell>
            <TableCell sx={{ border: '1px solid #000', bgcolor: '#f0f0f0', fontWeight: 'bold' }}>{displayData.chemical.mill.Mn || '-'}</TableCell>
            <TableCell sx={{ border: '1px solid #000', bgcolor: '#f0f0f0', fontWeight: 'bold' }}>{displayData.chemical.mill.P || '-'}</TableCell>
            <TableCell sx={{ border: '1px solid #000', bgcolor: '#f0f0f0', fontWeight: 'bold' }}>{displayData.chemical.mill.S || '-'}</TableCell>
            <TableCell sx={{ border: '1px solid #000', bgcolor: '#f0f0f0', fontWeight: 'bold' }}>{displayData.chemical.mill.Si || '-'}</TableCell>
            <TableCell sx={{ border: '1px solid #000', bgcolor: '#f0f0f0', fontWeight: 'bold' }}>{displayData.chemical.mill.Cr || '-'}</TableCell>
            <TableCell sx={{ border: '1px solid #000', bgcolor: '#f0f0f0', fontWeight: 'bold' }}>{displayData.chemical.mill.Ni || '-'}</TableCell>
            <TableCell sx={{ border: '1px solid #000', bgcolor: '#f0f0f0', fontWeight: 'bold' }}>{displayData.chemical.mill.Mo || '-'}</TableCell>
            <TableCell sx={{ border: '1px solid #000', bgcolor: '#f0f0f0', fontWeight: 'bold' }}>{displayData.chemical.mill.Al || '-'}</TableCell>
            <TableCell sx={{ border: '1px solid #000', bgcolor: '#f0f0f0', fontWeight: 'bold' }}>{displayData.chemical.mill.V || '-'}</TableCell>
          </TableRow>
        </TableBody>
      </Table>
    </TableContainer>
  );

  const renderHardnessResults = () => (
    <Grid container spacing={1}>
      {displayData.hardness.core_hardness.map((value, index) => (
        <Grid item xs={12} key={index}>
          <Box sx={{ display: 'flex', alignItems: 'center', borderBottom: '1px solid #ccc', py: 0.5 }}>
            <Typography sx={{ width: '100px' }}></Typography>
            <Typography sx={{ width: '100px' }}>{value}</Typography>
          </Box>
        </Grid>
      ))}
    </Grid>
  );

  return (
    <Box p={3}>
      {/* Search Section */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Typography variant="h6" gutterBottom>
          Search Raw Material Testing Report
        </Typography>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} sm={4}>
            <TextField
              fullWidth
              size="small"
              label="SRR No"
              value={searchParams.srr_no}
              onChange={(e) => setSearchParams({ ...searchParams, srr_no: e.target.value })}
            />
          </Grid>
          <Grid item xs={12} sm={4}>
            <TextField
              fullWidth
              size="small"
              label="Heat No"
              value={searchParams.heat_no}
              onChange={(e) => setSearchParams({ ...searchParams, heat_no: e.target.value })}
            />
          </Grid>
          <Grid item xs={12} sm={4}>
            <TextField
              fullWidth
              size="small"
              label="TC No"
              value={searchParams.tc_no}
              onChange={(e) => setSearchParams({ ...searchParams, tc_no: e.target.value })}
            />
          </Grid>
        </Grid>
        <Box sx={{ mt: 2, display: 'flex', gap: 2 }}>
          <Button
            variant="contained"
            onClick={handleSearch}
            startIcon={<SearchIcon />}
            disabled={isLoading}
          >
            {isLoading ? 'Searching...' : 'Search Report'}
          </Button>
          {displayData && (
            <>
              <Button
                variant="outlined"
                onClick={handlePrint}
                startIcon={<PrintIcon />}
              >
                Print
              </Button>
              <Button
                variant="outlined"
                color="success"
                onClick={handleExportPDF}
                startIcon={<DownloadIcon />}
              >
                Export PDF
              </Button>
            </>
          )}
        </Box>
      </Paper>

      {isLoading && (
        <Box display="flex" justifyContent="center" p={4}>
          <CircularProgress />
        </Box>
      )}

      {displayData && (
        <Paper ref={reportRef} sx={{ p: 3 }} className="report-content">
          {/* Report Header */}
          <Box sx={{ textAlign: 'center', mb: 2, borderBottom: '2px solid #000', pb: 1 }}>
            <Typography variant="h5" sx={{ fontWeight: 'bold' }}>
              KALYANI FORGE LTD
            </Typography>
            <Typography variant="h6">
              RAW MATERIAL TESTING REPORT
            </Typography>
            <Typography variant="body1">
              Production Steel (Repeat Heat)
            </Typography>
            <Typography variant="body2" sx={{ position: 'absolute', right: '20px', top: '20px' }}>
              SERIAL CONTROL : {displayData.srr_no}
            </Typography>
          </Box>

          {/* Basic Information */}
          <Grid container spacing={1} sx={{ mb: 2, fontSize: '12px' }}>
            <Grid item xs={12}>
              <Box sx={{ display: 'flex', gap: 3 }}>
                <Typography><strong>SRR NO:</strong> {displayData.srr_no}</Typography>
                <Typography><strong>dt:</strong> {displayData.srr_date}</Typography>
                <Typography><strong>MILL:</strong> {displayData.mill_name}</Typography>
                <Typography><strong>Specification:</strong> {displayData.specification}</Typography>
                <Typography><strong>Section:</strong> {displayData.section}</Typography>
              </Box>
            </Grid>
            <Grid item xs={12}>
              <Box sx={{ display: 'flex', gap: 3 }}>
                <Typography><strong>TC NO:</strong> {displayData.tc_no}</Typography>
                <Typography><strong>HEAT NO:</strong> {displayData.heat_no}</Typography>
                <Typography><strong>QTY:</strong> {displayData.quantity_nos} nos</Typography>
                <Typography><strong>WEIGHT:</strong> {displayData.weight} MT</Typography>
                <Typography><strong>SAMPLE NOS:</strong> ({displayData.sample_nos || 0})</Typography>
                <Typography><strong>ROUTE:</strong> {displayData.route}</Typography>
              </Box>
            </Grid>
            <Grid item xs={12}>
              <Box sx={{ display: 'flex', gap: 3 }}>
                <Typography><strong>VISUAL INSPECTION BEND:</strong> {displayData.hardness.visual_inspection}</Typography>
                <Typography><strong>AS RECD MICRO:</strong> {displayData.hardness.as_recd_micro ? 'Yes' : 'No'}</Typography>
                <Typography><strong>HEAT NO MARKING:</strong> {displayData.heat_marking}</Typography>
                <Typography><strong>MILL GRAIN SIZE:</strong> ({displayData.mill_grain_size})</Typography>
                <Typography><strong>ASTM GRAIN SIZE:</strong> {displayData.astm_grain_size || '-'}</Typography>
                <Typography><strong>STEEL CODE:</strong> {displayData.steel_code}</Typography>
              </Box>
            </Grid>
          </Grid>

          {/* Chemical Composition Table */}
          <Box sx={{ mb: 2 }}>
            <Typography variant="body1" sx={{ fontWeight: 'bold', mb: 1 }}>
              {displayData.hardness.macro_test_result}
            </Typography>
            {renderChemicalComposition()}
          </Box>

          {/* Hardness Results */}
          <Box sx={{ mb: 2 }}>
            {renderHardnessResults()}
          </Box>

          {/* Bottom Section */}
          <Grid container spacing={2} sx={{ mt: 3, borderTop: '1px solid #000', pt: 2 }}>
            <Grid item xs={6}>
              <Typography variant="body2"><strong>MECHANICAL PROPERTIES</strong></Typography>
              <Typography variant="body2">KFL</Typography>
              <Divider sx={{ my: 1 }} />
              <Typography variant="body2"><strong>TENSILE STRENGTH:</strong> {displayData.mechanical.tensile_strength || '-'}</Typography>
              <Typography variant="body2"><strong>YIELD STRENGTH:</strong> {displayData.mechanical.yield_strength || '-'}</Typography>
              <Typography variant="body2"><strong>%ELONGATION:</strong> {displayData.mechanical.elongation || '-'}</Typography>
              <Typography variant="body2"><strong>%AREA REDUCTION:</strong> {displayData.mechanical.area_reduction || '-'}</Typography>
            </Grid>
            <Grid item xs={6}>
              <Typography variant="body2"><strong>JOMINY HARDENABILITY</strong></Typography>
              <Grid container spacing={1}>
                <Grid item xs={4}>
                  <Typography variant="body2">DIST.</Typography>
                </Grid>
                <Grid item xs={4}>
                  <Typography variant="body2">MILL</Typography>
                </Grid>
                <Grid item xs={4}>
                  <Typography variant="body2">KFL</Typography>
                </Grid>
              </Grid>
            </Grid>
          </Grid>

          {/* Quality Observations */}
          <Box sx={{ mt: 2, p: 1, border: '1px solid #000' }}>
            <Typography variant="body2"><strong>SURFACE QUALITY OBSERVATIONS:</strong></Typography>
            <Typography variant="body2">1st Sample ({displayData.quality.surface_quality.first_sample}) bars: {displayData.quality.surface_quality.bars || '-'}</Typography>
            <Typography variant="body2">100 % Pickling & Grinding: {displayData.quality.pickling_grinding.percent_100 ? 'Yes' : 'No'}</Typography>
            <Typography variant="body2">Rejected Bars: {displayData.quality.pickling_grinding.rejected_bars}</Typography>
            <Typography variant="body2">Reinspection( ) bars: {displayData.quality.reinspection_bars || '-'}</Typography>
            <Typography variant="body2">STEP DOWN TEST: {displayData.quality.step_down_test || '-'}</Typography>
          </Box>

          {/* Remarks */}
          <Box sx={{ mt: 2 }}>
            <Typography variant="body2"><strong>REMARKS:</strong> {displayData.remarks}</Typography>
          </Box>

          {/* Status and Signature */}
          <Grid container spacing={2} sx={{ mt: 3 }}>
            <Grid item xs={4}>
              <Box sx={{ p: 1, border: '1px solid #000', textAlign: 'center' }}>
                <Typography variant="body1"><strong>STATUS</strong></Typography>
                <Chip
                  label={displayData.status}
                  color={displayData.status === 'ACCEPTED' ? 'success' : 'error'}
                  icon={displayData.status === 'ACCEPTED' ? <AcceptedIcon /> : <RejectedIcon />}
                  sx={{ mt: 1 }}
                />
              </Box>
            </Grid>
            <Grid item xs={4}>
              <Box sx={{ p: 1, border: '1px solid #000', textAlign: 'center' }}>
                <Typography variant="body1"><strong>AUTHORISED SIGN</strong></Typography>
                <Box sx={{ height: '40px' }}></Box>
              </Box>
            </Grid>
            <Grid item xs={4}>
              <Box sx={{ p: 1, border: '1px solid #000', textAlign: 'center' }}>
                <Typography variant="body1"><strong>DATE</strong></Typography>
                <Typography variant="body2" sx={{ mt: 1 }}>{displayData.date}</Typography>
              </Box>
            </Grid>
          </Grid>

          {/* Footer */}
          <Box sx={{ mt: 2, textAlign: 'left' }}>
            <Typography variant="caption">QA-FR-26</Typography>
          </Box>
        </Paper>
      )}

      {/* Print CSS */}
      <style jsx="true">{`
        @media print {
          .report-content {
            width: 100%;
            margin: 0;
            padding: 10px;
          }
          body * {
            visibility: hidden;
          }
          .report-content, .report-content * {
            visibility: visible;
          }
          .report-content {
            position: absolute;
            left: 0;
            top: 0;
          }
        }
      `}</style>
    </Box>
  );
};

export default RawMaterialTestingReport;